import isEqual from 'lodash.isequal';
import { VfsFetchError, type AppContext } from './appClient';
import { LocalFetchError } from './localRuntimeClient';
import {
  fetchFileContentStrict,
  fetchFilesFromFolderStrict,
  type VFSFetchOptions,
} from './vfsService';
import { Artifact } from './logicAppsApi';
import { telemetryService } from './telemetryService';
import type { VFSObject } from '@/types/vfs';
import type { Workflow } from '@/types/logicApps';
import { extractWorkflowName } from '@/types/logicApps';

const MAX_CONCURRENT_WORKFLOWS = 5;

export interface WorkflowDraftStatusFailure {
  workflowId: string;
  error: Error;
}

export interface WorkflowDraftStatusResult {
  byWorkflowId: Readonly<Record<string, boolean>>;
  failures: readonly WorkflowDraftStatusFailure[];
}

export interface WorkflowDraftStatusDependencies {
  listFiles: (opts: VFSFetchOptions, signal?: AbortSignal) => Promise<VFSObject[]>;
  fetchContent: (
    opts: VFSFetchOptions,
    fileName: string,
    signal?: AbortSignal,
  ) => Promise<unknown | null>;
}

const defaultDependencies: WorkflowDraftStatusDependencies = {
  listFiles: fetchFilesFromFolderStrict,
  fetchContent: fetchFileContentStrict,
};

const toTime = (value: string | undefined): number | undefined => {
  if (!value) return undefined;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : undefined;
};

export const detectUnpublishedWorkflowDraft = async (
  opts: VFSFetchOptions,
  files: readonly VFSObject[],
  dependencies: WorkflowDraftStatusDependencies = defaultDependencies,
  signal?: AbortSignal,
): Promise<boolean> => {
  const draft = files.find((file) => file.name === Artifact.DraftWorkflowFile);
  if (!draft) return false;

  const production = files.find((file) => file.name === Artifact.WorkflowFile);
  if (!production) return true;

  const draftTime = toTime(draft.mtime);
  const productionTime = toTime(production.mtime);
  if (draftTime === undefined || productionTime === undefined || draftTime <= productionTime) {
    return false;
  }

  const [draftContent, productionContent] = await Promise.all([
    dependencies.fetchContent(opts, Artifact.DraftWorkflowFile, signal),
    dependencies.fetchContent(opts, Artifact.WorkflowFile, signal),
  ]);
  if (draftContent === null || productionContent === null) {
    throw new Error('A workflow file disappeared while checking unpublished changes');
  }

  return !isEqual(draftContent, productionContent);
};

const asError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));

const toSafeDiagnostic = (
  error: Error,
): { error: Error; status?: number } => {
  const status =
    error instanceof VfsFetchError || error instanceof LocalFetchError
      ? error.status
      : undefined;
  const diagnostic = new Error(
    status === undefined
      ? 'Workflow draft status check failed'
      : `Workflow draft status check failed (HTTP ${status})`,
  );
  diagnostic.name = error.name;
  return {
    error: diagnostic,
    ...(status === undefined ? {} : { status }),
  };
};

const isRequestCancellation = (
  error: Error,
  signal: AbortSignal | undefined,
): boolean =>
  signal?.aborted === true ||
  error.name === 'AbortError' ||
  (
    error instanceof TypeError &&
    error.message === 'Failed to fetch' &&
    typeof document !== 'undefined' &&
    document.visibilityState === 'hidden'
  );

export const fetchWorkflowDraftStatuses = async (
  app: AppContext,
  workflows: readonly Workflow[],
  dependencies: WorkflowDraftStatusDependencies = defaultDependencies,
  signal?: AbortSignal,
): Promise<WorkflowDraftStatusResult> => {
  const byWorkflowId: Record<string, boolean> = {};
  const failures: WorkflowDraftStatusFailure[] = [];
  let nextIndex = 0;

  const worker = async (): Promise<void> => {
    while (nextIndex < workflows.length) {
      const index = nextIndex;
      nextIndex += 1;
      const workflow = workflows[index];
      if (!workflow) continue;

      const workflowId = workflow.id;
      const opts: VFSFetchOptions = {
        ...app,
        workflowName: extractWorkflowName(workflow.name),
      };

      try {
        signal?.throwIfAborted();
        const files = await dependencies.listFiles(opts, signal);
        byWorkflowId[workflowId] = await detectUnpublishedWorkflowDraft(
          opts,
          files,
          dependencies,
          signal,
        );
      } catch (error) {
        const normalizedError = asError(error);
        if (isRequestCancellation(normalizedError, signal)) {
          throw normalizedError;
        }
        byWorkflowId[workflowId] = false;
        failures.push({ workflowId, error: normalizedError });
        const diagnostic = toSafeDiagnostic(normalizedError);
        telemetryService.trackException(diagnostic.error, {
          context: 'workflow_list_draft_status',
          workflowId,
          ...(diagnostic.status === undefined
            ? {}
            : { httpStatus: diagnostic.status }),
        });
        console.error(
          `[fetchWorkflowDraftStatuses] Failed for workflow ${workflowId}:`,
          diagnostic.error,
        );
      }
    }
  };

  await Promise.all(
    Array.from(
      { length: Math.min(MAX_CONCURRENT_WORKFLOWS, workflows.length) },
      () => worker(),
    ),
  );

  return { byWorkflowId, failures };
};
